import React, { useEffect, useState } from "react";
import Module from "../../../../layout/Module";
import { Box, Step, StepLabel, Stepper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ButtonStartIcon from "../../../../button/ButtonStartIcon";

import useClasses from "../../../../../hooks/classes/useClasses";
import { Alert } from "@mui/material";
import AlertSaveButton from "../../../../button/AlertSaveButton";
import isEmpty from "lodash/isEmpty";
import { toggleAlertModule } from "../../../../../store/actions/alert.actions";
import {
  updateClassFormField,
  updateClassFormError,
  updateClassPreview,
} from "../../../../../store/actions/class.actions";
import IconTooltipButton from "../../../../button/IconTooltipButton";
import CardsBasicInformation from "../CardsBasicInformation/CardsBasicInformation";
import { updateFilter } from "../../../../../store/actions/filter.actions";
import FormBasicInformation from "../FormBasicInformation/FormBasicInformation";
import FormCurriculumElements from "../FormCurriculumElements/FormCurriculumElements";
import FormSubject from "../FormSubject/FormSubject";
import FormUsers from "../FormUsers/FormUsers";
import FormPreview from "../FormPreview/FormPreview";
import FormUnitOfLearning from "../FormUnitOfLearning/FormUnitOfLearning";
import { setCurrentCurriculum } from "../../../../../store/actions/assessment.actions";

const Create = () => {
  const { createClass, validateFormClasses } = useClasses();
  const dispatch = useDispatch();
  const existLinceseAssessment = useSelector(
    (state) =>
      state?.tenant?.license?.find((license) => license.code === "assessment")
  );
  const steps = existLinceseAssessment ? [
    "Basic Information",
    "Add teachers",
    "Add students",
    "Units of learning",
    "Preview",
  ]: [
    "Basic Information",
    "Add teachers",
    "Add students",
    "Preview"
  ]
  const formData = useSelector((state) => state?.class?.form || {});
  const formError = useSelector((state) => state?.class?.formError || {});
  const alertSave = useSelector((state) => state?.alert?.save || {});

  const permission = useSelector(
    (state) => state?.session?.permissions?.class || {}
  );
  const { curriculumId, step, studentId } = useParams();
  const redirectStudentId = studentId ? `${studentId}/` : "";
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [loading, toggleLoading] = useState(false);

  const onCancel = () => {
    navigate(`/dashboard/${redirectStudentId}classes/${curriculumId}`);
    dispatch(updateFilter("class", {}));
    dispatch(toggleAlertModule(null));
  };
  const onRedirect = (step) => {
    navigate(
      `/dashboard/${redirectStudentId}classes/${curriculumId}/create/step/${step}`
    );
  };
  const onChangeAlert = (key, value) => {
    dispatch(updateClassFormField(key, value));

    if (key === "curriculumId") {
      dispatch(setCurrentCurriculum(value));
      dispatch(updateClassFormField("subjectId", null));
      dispatch(updateClassFormField("unitOfLearningId", null));
      dispatch(updateClassFormField("curriculumElements", {}));
    }
    if (key === "add") {
      if (step === "1") {
        dispatch(updateClassFormField("teachers", value));
      }
      if (step === "2") {
        dispatch(updateClassFormField("students", value));
      }
    }
    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(
        toggleAlertModule(
          false,
          "Classes",
          "create",
          onSave,
          alertSave.onDontSave
        )
      );
    }
  };

  const onNext = () => {
    setError(null);
    const errors = validateFormClasses(formData, `create-${step}`);
    if (errors.error) {
      if (errors.message) {
        setError({ message: errors.message });
      } else {
        dispatch(updateClassFormError(errors));
      }
    } else {
      onRedirect(parseInt(step) + 1);
    }
  };
  const onSave = async (e) => {
    e.preventDefault();
    toggleLoading(true);
    try {
      let result = await createClass(formData.curriculumId, formData);
      if (result.error) {
        setError(result);
      } else {
        onCancel();
      }
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    if (isEmpty(formData)) {
      onCancel();
    }
  }, [formData]);
  return (
    <>
      <Module.BodyHeader className="border-b border-b-2 border-primary flex justify-between">
        <Stepper color="primary" activeStep={parseInt(step)} className="w-full">
          {steps.map((step, index) => (
            <Step key={`step-${index}`}>
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className="flex items-center justify-end gap-4">
          <div className="min-w-25px">
            {parseInt(step) > 0 && (
              <IconTooltipButton
                disabled={loading}
                title="Previous"
                icon="chevron-left"
                classNameIcon="text-lg"
                style={{
                  borderRadius: "0.5rem",
                  padding: "0.45rem",
                  color: "var(--primary)",
                  backgroundColor: "#fff",
                  width: 25,
                  height: 25,
                }}
                className="shadow-sm"
                onClick={() => onRedirect(parseInt(step) - 1)}
              />
            )}
          </div>
          <div className="min-w-100px">
            {parseInt(step) === steps.length - 1 ? (
              permission.created && (
                <ButtonStartIcon
                  disabled={loading}
                  loading={loading}
                  onClick={onSave}
                  startIcon={["fal", "check"]}
                  color="primary"
                  className="w-full"
                >
                  Save
                </ButtonStartIcon>
              )
            ) : (
              <IconTooltipButton
                disabled={loading}
                title="Next"
                icon="chevron-right"
                classNameIcon="text-lg"
                style={{
                  borderRadius: "0.5rem",
                  padding: "0.45rem",
                  color: "var(--primary)",
                  backgroundColor: "#fff",
                  width: 25,
                  height: 25,
                }}
                className="shadow-sm"
                onClick={onNext}
              />
            )}
          </div>
          <AlertSaveButton
            submodule="Classes"
            disabled={loading}
            icon={["fal", "times"]}
            title="Cancel"
            color="primary"
            variant="outlined"
            className="shadow-sm w-full"
            onDontSave={onCancel}
          />
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer
        loading={loading}
        classNameWrapper={loading ? "" : "overflow-auto"}
      >
        {error?.message && (
          <Alert
            severity="error"
            className="absolute left-0 right-0 z-12"
            onClose={() => setError(null)}
          >
            {error?.message}
          </Alert>
        )}
        <Box className="absolute inset-0">
          {step === "2" && (
            <IconTooltipButton
              disabled={loading}
              title="Preview"
              icon="expand-alt"
              classNameIcon="text-xl"
              style={{
                padding: "0.45rem",
                backgroundColor: "var(--primary)",
                color: "var(--primary-foreground)",
                position: "absolute",
                width: 35,
                height: 35,
              }}
              className="shadow-sm  z-10 right-0 top-0"
              onClick={() => dispatch(updateClassPreview("create"))}
            />
          )}
          {existLinceseAssessment && ["1", "2", "3"].includes(step) && <CardsBasicInformation />}
          {!existLinceseAssessment && ["1", "2"].includes(step) && <CardsBasicInformation />}
          {step === "0" && (
            <FormBasicInformation
              showTitle={true}
              form={formData}
              errors={formError || {}}
              onChange={onChangeAlert}
            />
          )}
          {/* {existLinceseAssessment && step === "0" && (
            <FormCurriculumElements
              showTitle={true}
              form={formData}
              errors={formError || {}}
              onChange={onChangeAlert}
              curriculumId={curriculumId}
            />
          )} */}
          {existLinceseAssessment && step === "0" && (
            <FormSubject
              showTitle={true}
              form={formData}
              errors={formError || {}}
              onChange={onChangeAlert}
              curriculumId={curriculumId}
            />
          )}
          {step === "1" && (
            <FormUsers
              type="add-teachers"
              userType={"teachers"}
              onChangeAlert={onChangeAlert}
            />
          )}
          {step === "2" && (
            <FormUsers
              type="add-students"
              userType={"students"}
              onChangeAlert={onChangeAlert}
            />
          )}
          {step === "3" && (
            existLinceseAssessment ? (
            <FormUnitOfLearning
              onChangeAlert={onChangeAlert}
              form={formData}
            />
          ) : (
            <FormPreview />
          ))}
          {step === "4" && (
            <FormPreview />
          )}
        </Box>
      </Module.BodyContainer>
    </>
  );
};

export default Create;
